import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import {
  createNameWithVersionMap,
  GenericElement,
  NotFoundException,
  Pagination,
  SearchFilter,
  TableCellProps,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { parseEntityRef } from '@mainstage/catalog-model';
import { useApi } from '@mainstage/core-plugin-api';
import { Box, Typography } from '@material-ui/core';
import { FC, ReactNode, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../scopeProvider.factory';
import {
  MyProvider,
  ProviderFilters,
  ScopeProviderType,
  SystemFilters,
} from '../types';
import { getLabelAndValue } from '../../utils';
import { CatalogApi } from '@mainstage/plugin-catalog-react';

export type SystemTypePSElement = GenericElement & {};

interface SystemTypeContextType
  extends ScopeProviderType<GenericElement, ProviderFilters> {}

const [useSystemContext, SystemTypeContextProvider] =
  contextProviderFactory<SystemTypeContextType>();

export default useSystemContext;

export interface SystemTypeProviderProps {
  children: ReactNode;
}

export const SystemTypeFiltersSection = () => {
  const { filters, changeFilters, resetFilters } = useSystemContext();

  return (
    <WbResponsiveTableToolbar
      onClear={resetFilters}
      style={{ marginTop: '16px' }}
      marginBottom
    >
      <WbResponsiveTableToolbar.Filters>
        <SearchFilter
          key="search"
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};

export const SystemTypeProvider: FC<SystemTypeProviderProps> = props => {
  const { children } = props;
  const [filters, setFilters] = useState<SystemFilters>({});
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    currentPage: 0,
  });
  const practiceShaperApi = useApi(practiceShaperApiRef);
  const defaultColumns: TableCellProps<GenericElement>[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'name',
        fieldRender: (field: GenericElement) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.displayName}</Typography>
            </Box>
          );
        },
      },
      {
        headerName: 'Description',
        field: 'description',
        fieldRender: (field: GenericElement) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.description}</Typography>
            </Box>
          );
        },
      },
    ];
  }, []);

  const systemTypeState = useAsync(async () => {
    const result = await practiceShaperApi.getSystemTypes({
      filter: { isAbstract: false },
    });

    setCount(result.items.length);

    const adaptedValues = result.items.map(entity => getLabelAndValue(entity));

    const nameWithVersionMap = createNameWithVersionMap(
      adaptedValues,
      'displayName',
    );

    return adaptedValues.map(val => {
      return {
        ...val,
        displayName: nameWithVersionMap[val.id].nameWithVersion,
      };
    });
  }, [filters]);

  const processedData = useMemo(() => {
    if (!systemTypeState.value) {
      return { loading: false, error: null, value: [] };
    }

    const filteredData = systemTypeState.value.filter(
      item =>
        item.name
          ?.toLowerCase()
          .includes(filters.search?.toLowerCase() ?? '') ||
        item.description
          ?.toLowerCase()
          .includes(filters.search?.toLowerCase() ?? ''),
    );

    const startIndex = pagination.currentPage * pagination.limit;
    const endIndex = startIndex + pagination.limit;

    return {
      loading: systemTypeState.loading,
      error: systemTypeState.error,
      value: filteredData.slice(startIndex, endIndex),
    };
  }, [
    systemTypeState.value,
    systemTypeState.loading,
    systemTypeState.error,
    filters.search,
    pagination.currentPage,
    pagination.limit,
  ]);

  const getRowId = (row: any) => row.id;

  return (
    <SystemTypeContextProvider
      value={
        {
          entitiesState: processedData,
          columns: defaultColumns,
          getRowId,
          filters,
          changeFilters: <K extends keyof SystemFilters>(
            key: K,
            filterValue: SystemFilters[K],
          ) => {
            setPagination(p => ({ ...p, currentPage: 0 }));
            setFilters(f => ({ ...f, [key]: filterValue }));
          },
          resetFilters: () => {
            setFilters({});
          },
          pagination,
          setPagination,
          count,
        } as unknown as ScopeProviderType<GenericElement, ProviderFilters>
      }
    >
      {children}
    </SystemTypeContextProvider>
  );
};

export class SystemTypeHandlerProvider
  implements MyProvider<GenericElement, ProviderFilters>
{
  private readonly catalogApi: CatalogApi;

  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  useContext = useSystemContext;

  renderContextProvider(children: ReactNode) {
    return <SystemTypeProvider>{children}</SystemTypeProvider>;
  }

  filtersSection = SystemTypeFiltersSection;

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) ?? ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'systemtype') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity) return getLabelAndValue(entity);
        throw new NotFoundException(
          `Cannot find a System type entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
